import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { localeFor } from '../utils/timeFormat';
import { openSupport } from './SupportWidget';
import './Notifications.css';

const Notifications = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [respondLinks, setRespondLinks] = useState({});

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get('/api/notifications');
      setNotifications(response.data.notifications);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await axios.post(`/api/notifications/${notificationId}/mark_as_read`);
      fetchNotifications();
    } catch (err) {
      alert(t('notifications.markReadError') + ': ' + (err.response?.data?.error || err.message));
    }
  };

  const handleAccept = async (notificationId) => {
    try {
      const response = await axios.post(`/api/notifications/${notificationId}/accept_invitation`);
      alert(response.data.message);
      fetchNotifications();
      window.location.reload();
    } catch (err) {
      alert(t('notifications.acceptError') + ': ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDecline = async (notificationId) => {
    try {
      const response = await axios.post(`/api/notifications/${notificationId}/decline_invitation`);
      alert(response.data.message);
      fetchNotifications();
    } catch (err) {
      alert(t('notifications.declineError') + ': ' + (err.response?.data?.error || err.message));
    }
  };

  // Приглашение на событие: принять — значит зарегистрироваться, после чего
  // открываем страницу события
  const handleEventAccept = async (notification) => {
    try {
      await axios.post(`/api/notifications/${notification.id}/accept_event_invitation`);
      navigate(`/events/${notification.event_id}`);
    } catch (err) {
      alert(t('notifications.acceptError') + ': ' + (err.response?.data?.error || err.message));
    }
  };

  const handleEventDecline = async (notification) => {
    try {
      await axios.post(`/api/notifications/${notification.id}/decline_event_invitation`);
      fetchNotifications();
    } catch (err) {
      alert(t('notifications.declineError') + ': ' + (err.response?.data?.error || err.message));
    }
  };

  const handleWildCardApprove = async (notification) => {
    try {
      await axios.post(`/api/wild_cards/${notification.wild_card_id}/approve`);
      fetchNotifications();
    } catch (err) {
      alert(t('notifications.wild_card.approveError') + ': ' + (err.response?.data?.error || err.message));
    }
  };

  const handleWildCardReject = async (notification) => {
    try {
      await axios.post(`/api/wild_cards/${notification.wild_card_id}/reject`);
      fetchNotifications();
    } catch (err) {
      alert(t('notifications.wild_card.rejectError') + ': ' + (err.response?.data?.error || err.message));
    }
  };

  const handleWildCardRespond = async (notification) => {
    const link = respondLinks[notification.id]?.trim();
    if (!link) {
      alert(t('notifications.wild_card.respondLinkRequired'));
      return;
    }
    try {
      await axios.post(`/api/wild_cards/${notification.wild_card_id}/approve`, { event_link: link });
      setRespondLinks((prev) => { const next = { ...prev }; delete next[notification.id]; return next; });
      fetchNotifications();
    } catch (err) {
      alert(t('notifications.wild_card.respondError') + ': ' + (err.response?.data?.error || err.message));
    }
  };

  const getContent = (notification) => {
    if (language === 'it' && notification.content_it) return notification.content_it;
    if (language === 'de' && notification.content_de) return notification.content_de;
    if (language === 'es' && notification.content_es) return notification.content_es;
    if (language === 'fr' && notification.content_fr) return notification.content_fr;
    if (language === 'cs' && notification.content_cs) return notification.content_cs;
    if (language !== 'ru' && notification.content_en) return notification.content_en;
    return notification.content;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString(localeFor(language));
  };

  const renderActions = (notification) => {
    const type = notification.notification_type;

    if (type === 'wild_card_submitted') {
      if (notification.read) return <span className="badge-read">{t('notifications.read')}</span>;
      return (
        <>
          {notification.wild_card_event_link && (
            <a
              href={notification.wild_card_event_link}
              target="_blank"
              rel="noopener noreferrer"
              className="wildcard-notif-link"
            >
              {t('notifications.wild_card.viewRide')}
            </a>
          )}
          <button onClick={() => handleWildCardApprove(notification)} className="btn btn-accept">
            {t('notifications.wild_card.approve')}
          </button>
          <button onClick={() => handleWildCardReject(notification)} className="btn btn-decline">
            {t('notifications.wild_card.reject')}
          </button>
        </>
      );
    }

    if (type === 'wild_card_requested') {
      if (notification.read) return <span className="badge-read">{t('notifications.read')}</span>;
      return (
        <div className="wildcard-respond-form">
          <input
            type="url"
            value={respondLinks[notification.id] || ''}
            onChange={(e) => setRespondLinks((prev) => ({ ...prev, [notification.id]: e.target.value }))}
            placeholder="https://riders.rouvy.com/events/..."
            className="wildcard-respond-input"
          />
          <button onClick={() => handleWildCardRespond(notification)} className="btn btn-accept">
            {t('notifications.wild_card.respond')}
          </button>
        </div>
      );
    }

    if (type === 'wild_card_approved') {
      return (
        <>
          {notification.wild_card_event_link && (
            <a
              href={notification.wild_card_event_link}
              target="_blank"
              rel="noopener noreferrer"
              className="wildcard-notif-link"
            >
              {t('notifications.wild_card.openEvent')}
            </a>
          )}
          {!notification.read && (
            <button onClick={() => handleMarkAsRead(notification.id)} className="btn btn-mark-read">
              {t('notifications.markRead')}
            </button>
          )}
          {notification.read && <span className="badge-read">{t('notifications.read')}</span>}
        </>
      );
    }

    if (type === 'wild_card_rejected') {
      if (notification.read) return <span className="badge-read">{t('notifications.read')}</span>;
      return (
        <button onClick={() => handleMarkAsRead(notification.id)} className="btn btn-mark-read">
          {t('notifications.markRead')}
        </button>
      );
    }

    if (notification.requires_action) {
      return (
        <>
          <button onClick={() => handleAccept(notification.id)} className="btn btn-accept">
            {t('notifications.accept')}
          </button>
          <button onClick={() => handleDecline(notification.id)} className="btn btn-decline">
            {t('notifications.decline')}
          </button>
        </>
      );
    }

    if (!notification.read) {
      return (
        <button onClick={() => handleMarkAsRead(notification.id)} className="btn btn-mark-read">
          {t('notifications.markRead')}
        </button>
      );
    }

    return <span className="badge-read">{t('notifications.read')}</span>;
  };

  if (loading) {
    return <div className="notifications-container"><div className="loading">{t('common.loading')}</div></div>;
  }

  if (error) {
    return <div className="notifications-container"><div className="error">{error}</div></div>;
  }

  return (
    <div className="notifications-container">
      <h2>{t('notifications.title')}</h2>

      {notifications.length === 0 ? (
        <div className="no-notifications">
          <p>{t('notifications.noNotifications')}</p>
        </div>
      ) : (
        <div className="notifications-list">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className={`notification-item ${!notification.read ? 'unread' : ''}`}
            >
              <div className="notification-content">
                <p className="notification-text">{getContent(notification)}</p>
                <small className="notification-date">{formatDate(notification.created_at)}</small>
              </div>
              <div className="notification-actions">
                {notification.notification_type === 'event_invitation' ? (
                  <>
                    <button
                      onClick={() => navigate(`/events/${notification.event_id}`)}
                      className="btn btn-mark-read"
                    >
                      {t('notifications.event.open')}
                    </button>
                    {notification.read ? (
                      <span className="badge-read">{t('notifications.read')}</span>
                    ) : (
                      <>
                        <button
                          onClick={() => handleEventAccept(notification)}
                          className="btn btn-accept"
                        >
                          {t('notifications.event.participate')}
                        </button>
                        <button
                          onClick={() => handleEventDecline(notification)}
                          className="btn btn-decline"
                        >
                          {t('notifications.decline')}
                        </button>
                      </>
                    )}
                  </>
                ) : notification.requires_action ? (
                  <>
                    <button
                      onClick={() => handleAccept(notification.id)}
                      className="btn btn-accept"
                    >
                      {t('notifications.accept')}
                    </button>
                    <button
                      onClick={() => handleDecline(notification.id)}
                      className="btn btn-decline"
                    >
                      {t('notifications.decline')}
                    </button>
                  </>
                ) : notification.notification_type === 'workout_shared' && notification.workout_id ? (
                  <button
                    onClick={() => navigate(`/workouts/${notification.workout_id}`)}
                    className="btn btn-accept"
                  >
                    {t('workouts.share.openWorkout')}
                  </button>
                ) : ['support_request', 'support_reply'].includes(notification.notification_type) ? (
                  <button
                    onClick={() => openSupport(notification.support_ticket_id)}
                    className="btn btn-accept"
                  >
                    {t('support.openConversation')}
                  </button>
                ) : notification.notification_type === 'rmc_join_request' && !notification.read ? (
                  <>
                    <button
                      onClick={() => { handleMarkAsRead(notification.id); navigate('/rmc/admin'); }}
                      className="btn btn-accept"
                    >
                      {t('rmc.admin.joinRequestsTab')}
                    </button>
                    <button
                      onClick={() => handleMarkAsRead(notification.id)}
                      className="btn btn-mark-read"
                    >
                      {t('notifications.markRead')}
                    </button>
                  </>
                ) : !notification.read ? (
                  <button
                    onClick={() => handleMarkAsRead(notification.id)}
                    className="btn btn-mark-read"
                  >
                    {t('notifications.markRead')}
                  </button>
                ) : (
                  <span className="badge-read">{t('notifications.read')}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Notifications;